import { useEffect, useRef } from 'react'
import { readGarden, writeGarden, type GardenState } from './garden.js'
import { checkAuthOnce } from './authCheck.js'

export function useGardenSync(lang: string) {
  const syncing = useRef(false)
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    let mounted = true

    async function loadFromServer() {
      try {
        if (!(await checkAuthOnce())) return
        const res = await fetch(`/api/data/garden-state?lang=${lang}`)
        if (!res.ok) return
        const { state } = await res.json()
        if (!state) return

        const local = readGarden(lang)
        const serverScore = ((state.stats?.runs ?? 0) as number) * 10000 + ((state.stats?.totalCorrect ?? 0) as number)
        const localScore = local.stats.runs * 10000 + local.stats.totalCorrect

        if (serverScore > localScore && mounted) {
          // Write silently to avoid triggering the c8:garden-saved event,
          // which would immediately re-upload data the server just sent us.
          writeGarden(lang, state as GardenState, { silent: true })
          window.dispatchEvent(new Event('c8:garden-loaded'))
        }
      } catch {}
    }

    async function syncToServer() {
      if (syncing.current) return
      syncing.current = true
      try {
        if (!(await checkAuthOnce())) return
        const state = readGarden(lang)
        await fetch(`/api/data/garden-state?lang=${lang}`, {
          method: 'PUT',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify({ state }),
        })
      } catch {} finally {
        syncing.current = false
      }
    }

    function onGardenSaved(e: Event) {
      const detail = (e as CustomEvent).detail
      if (detail?.lang && detail.lang !== lang) return
      if (debounceRef.current) clearTimeout(debounceRef.current)
      debounceRef.current = setTimeout(syncToServer, 3000)
    }

    loadFromServer()
    window.addEventListener('c8:garden-saved', onGardenSaved)
    return () => {
      mounted = false
      window.removeEventListener('c8:garden-saved', onGardenSaved)
      if (debounceRef.current) clearTimeout(debounceRef.current)
    }
  }, [lang])
}
